import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { Ticket } from '../lib/types';
import { useApiResource } from './useApiResource';

const PRIORITIES: Record<string, number> = {Urgent: 0, High: 1, Medium: 2, Low: 3};
const SORTERS: Record<string, (a: Ticket, b: Ticket) => number> = {
  'Priority': (a, b) => PRIORITIES[a.priority] - PRIORITIES[b.priority],
  'Oldest first': (a, b) => a.created_at.localeCompare(b.created_at),
  'Newest first': (a, b) => b.created_at.localeCompare(a.created_at),
};

export function useTicketLog() {
  const resource = useApiResource<Ticket[]>('/tickets', []);
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('all');
  const [priority, setPriority] = useState('');
  const [sort, setSort] = useState('Newest first');
  const [deleting, setDeleting] = useState<Ticket | null>(null);
  const [busy, setBusy] = useState(false);
  const tickets = resource.data;
  const filtered = useMemo(() => {
    const matches = tickets.filter(ticket => {
      if (status !== 'all' && ticket.status !== status) return false;
      if (priority && ticket.priority !== priority) return false;
      return `${ticket.title} ${ticket.message} ${ticket.number}`.toLowerCase().includes(query.toLowerCase());
    });
    return matches.sort(SORTERS[sort]);
  }, [tickets, status, priority, query, sort]);
  const counts = useMemo(() => tickets.reduce<Record<string, number>>((values, ticket) => {
    values[ticket.status] = (values[ticket.status] || 0) + 1;
    return values;
  }, {all: tickets.length}), [tickets]);

  const remove = async () => {
    if (!deleting || busy) return;
    setBusy(true);
    try {
      await api(`/tickets/${deleting.id}`, 'DELETE');
      setDeleting(null);
      await resource.refresh();
      toast.success('Ticket deleted.');
    } catch (error: any) { toast.error(error.message); }
    finally { setBusy(false); }
  };
  const clearFilters = () => {setQuery(''); setStatus('all'); setPriority('');};
  return {...resource, tickets, filtered, counts, query, setQuery, status, setStatus,
    priority, setPriority, sort, setSort, deleting, setDeleting, busy, remove, clearFilters};
}